import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { UserServices } from "../../../services/user/user.services";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";

const UsersComponent = () => {
    const navigate = useNavigate();
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [filterText, setFilterText] = useState("");
    const [usersData, setUsersData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 5;
    const [showModal, setShowModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    useEffect(() => {
        const fetchUsersData = async () => {
            try {
                const response = await UserServices.getAllUsers(selectedStore);
                if (response) {
                    setUsersData(response);
                }
            }
            catch (error) {
                console.error("Error fetching users");
                setUsersData([])
            }
        }
        fetchUsersData();
    }, []);

    const columns = [
        {
            name: "UserName",
            selector: row => row.username,
            sortable: true,
        },
        {
            name: "Full Name",
            selector: row => `${row.firstName} ${row.lastName}`,
            sortable: true,
            wrap: true,
        },
        {
            name: "Email",
            selector: row => row.email,
            sortable: true,
            width: "15%"
        },
        {
            name: "Phone",
            selector: row => row.phone,
            sortable: true
        },
        {
            name: "Role",
            selector: row => row.role?.roleName,
            sortable: true,
        },
        {
            name: "Max Discount(%)",
            selector: row => row.maxDiscount,
            sortable: true,
        },
        {
            name: "Active?",
            selector: row => row.isActive,
            sortable: true,
            cell: row => (
                <button className={`btn btn-light radius-30 px-4 ${row.isActive ? 'text-success' : 'text-danger'}`}>
                    {row.isActive === true ? "Active" : "Inactive"}
                </button>
            ),
            center: true,
        },
        {
            name: "Action",
            button: true,
            width: "15%",
            cell: row => (
                <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={() => handleEditClick(row)}>Edit</button>
                    <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>Delete</button>
                </div>
            ),
        },
    ];

    const handleEditClick = (row) => {
        try {
            navigate('/users/edit', { state: { userId: row?.userId } });
        } catch (error) {
            console.error('Error navigating to edit page:', error);
        }
    };

    const handleDeleteClick = (item) => {
        setUserToDelete(item);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            if (userToDelete) {
                await UserServices.deleteUser(userToDelete.userId);
                setUsersData(prevData => prevData.filter(user => user.userId !== userToDelete.userId));
                handleNotify("User deleted successfully!", TOASTER_TYPE.SUCCESS, TOASTER_POSITION.TOP_RIGHT);
            }
            setShowModal(false);
        } catch (error) {
            handleNotify(error || 'Error while deleting User.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
    };

    const filteredData = usersData.filter(item =>
        Object.keys(item).some(key =>
            item[key]?.toString().toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleNewClick = () => {
        navigate("/users/create", { state: { pageNumber: currentPage } });
    };

    return (
        <>
            <div className="page-wrapper m-0 p-4">
                <div className="page-content">
                    <div className="page-breadcrumb d-flex align-items-center mb-3">
                        <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4">
                            Users
                        </div>
                        <div className="ms-auto">
                            <button className="btn btn-primary rounded-3 px-4" onClick={handleNewClick}>New +</button>
                        </div>
                    </div>
                    <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                        <div className="card-body">
                            <div className="d-flex flex-column flex-md-row mb-3">
                            <div className="btn-group mb-3 mb-md-0 me-2">
                                    <button className="btn btn-outline-primary" onClick={() => handleCopy(filteredData)}>Copy</button>
                                    <button className="btn btn-outline-success" onClick={() => handleExcel(filteredData)}>Excel</button>
                                    <button className="btn btn-outline-danger" onClick={() => handlePDF(filteredData)}>PDF</button>
                                    <button className="btn btn-outline-info" onClick={handlePrint}>Print</button>
                                </div>

                                <div className="d-flex ms-auto align-items-center">
                                    <div className="me-2">
                                        <span className="fw-bold">Search:</span>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control rounded-3"
                                        placeholder="Search 🔍..."
                                        value={filterText}
                                        onChange={e => setFilterText(e.target.value)}
                                    />
                                </div>
                            </div>

                            {currentData && (
                                <div className="table-responsive">
                                    <DataTable
                                        columns={columns}
                                        data={currentData}
                                        fixedHeader
                                        responsive
                                        highlightOnHover
                                        sortIcon={<i className="bx bx-sort" />}
                                    />
                                </div>
                            )}

                            <div className="d-flex mt-4 justify-content-end">
                                <div className="btn-group">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        Prev
                                    </button>
                                    {Array.from({ length: totalPages }, (_, index) => (
                                        <button
                                            key={index}
                                            className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => handlePageChange(index + 1)}
                                        >
                                            {index + 1}
                                        </button>
                                    ))}
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showModal &&
                <DeleteModelPopupComponent
                    show={showModal}
                    onClose={handleModalClose}
                    onConfirm={handleDeleteConfirm}
                    title="Confirm"
                    confirmText="Delete"
                    Message="Are you sure want to delete selected user?"
                />
            }
        </>
    );
};

export default UsersComponent;
